import { empty } from "../frontendlib/dom_utils";
import { clone, escapeRegExp, type } from "../frontendlib/utils";
import EventListener from "../frontendlib/event_listener";

import ClickInteraction from "../lib/click_interaction";

import Container from "../container";
import { clickAreaTypeFromEvent } from "./cta";
import LiftoffMRAID from "./liftoff_mraid";
import addDeadzones from "./addDeadzones";

class Madlib {
  static init() {
    EventListener.pubsubify(this);

    this.rootEl = null;
    this.config = null;

    this.loaded = false;
    this.containerByID = {};
    this.allContainers = [];
    this.rootContainer = null;

    // TODO(vsingh): Remove hacky global once we are more clear on how Madlib resize events should
    //   be handled.
    this.didEverApplyLetterboxStyles = false;

    this.productIndex = 0;

    this.on("navigation-click", (queryParams, clickAreaType) => {
      Liftoff.handleClick(queryParams, clickAreaType);
    });
  }

  /**
   * Returns a product cycling indefinitely.
   */
  static getNextProduct() {
    const { products = [] } = Liftoff.env;

    const { productIndex } = this;
    this.productIndex += 1;

    return products[productIndex % products.length];
  }

  static error(e = new Error("Unspecified Madlib error.")) {
    Liftoff.handleMadlibError(e);
  }

  static load(rootEl, config) {
    this.rootEl = rootEl;
    this.config = config;
    this.applyExchangePolicies();
    this.loadDOM();
    this.addEventListeners();
    this.coordinate();
    this.layout();

    Liftoff.onLoadCompleted();
  }

  static applyExchangePolicies() {
    const animationDurationLimit = Liftoff.getAnimationDurationLimit();
    if (animationDurationLimit > 0) {
      setTimeout(() => {
        Madlib.allContainers.forEach((container) => {
          const { content } = container;
          if (content == null) return;
          container.layout().then(() => {
            content.stop();
          });
        });
      }, animationDurationLimit);
    }
  }

  static loadDOM() {
    this.layoutEl = document.createElement("div");
    this.layoutEl.id = "liftoff-layout";
    this.rootEl.appendChild(this.layoutEl);

    this.clickEl = document.createElement("div");
    this.clickEl.id = "liftoff-click";
    ClickInteraction.addListeners(this.clickEl, (e) => {
      this.handleNavigationClick(e);
    });
    this.rootEl.appendChild(this.clickEl);

    const { is_interstitial, is_reengagement } = Liftoff.bidContext;
    // TODO(oscar): Cleanup test code once crserve-deadzone experiment is over.
    const isDeadzoneExperiment = Liftoff.hasTestGroup(
      "crserve-deadzone-v2-experiment"
    );
    const useDeadzones =
      is_interstitial && (!is_reengagement || isDeadzoneExperiment);
    if (useDeadzones) {
      addDeadzones(isDeadzoneExperiment, this.rootEl);
    }
  }

  static handleNavigationClick(e, queryParams = {}) {
    this.emit("navigation-click", queryParams, clickAreaTypeFromEvent(e));
  }

  static addEventListeners() {
    window.addEventListener("resize", () => {
      this.layout();
    });
  }

  static coordinate() {
    this.on("wait", ({ timeMS }) => {
      return new Promise((resolve) => setTimeout(resolve, Number(timeMS)));
    });
  }

  static getEvents() {
    return [{ name: "beforeVisible" }].map((e) => {
      e.type = "madlib";
      return e;
    });
  }

  static getActions() {
    return [
      {
        name: "wait",
        data: ["timeMS"],
      },
    ].map((a) => {
      a.type = "madlib";
      return a;
    });
  }

  static getCreativeConfig() {
    // TODO(cory) Implement rendering for multiple scenes. For now, all configs have exactly one scene.
    return this.config.scenes[0].layoutContent;
  }

  static configureLetterbox(letterboxConfig) {
    const useDarkRootBackground = letterboxConfig.useDarkRootBackground();
    const useLayoutBackground = letterboxConfig.useLayoutBackground();

    this.rootEl.classList.toggle(
      "letterbox-dark-background",
      useDarkRootBackground
    );
    this.layoutEl.classList.toggle(
      "letterbox-layout-background",
      useLayoutBackground
    );

    this.didEverApplyLetterboxStyles = true;

    Object.assign(
      this.clickEl.style,
      letterboxConfig.getStyleForClickElement()
    );
  }

  static layout() {
    if (this.rootContainer != null) {
      this.rootContainer.invalidate();
    }

    this.reset();

    const letterboxConfig = Liftoff.getLetterboxConfig();
    var rootElementConstraints = null;

    if (letterboxConfig.enabled()) {
      this.configureLetterbox(letterboxConfig);
      rootElementConstraints =
        letterboxConfig.getLetterboxConstraintsForMadlibRootElement();
    }

    this.parseCreativeConfig(
      this.layoutEl,
      this.getCreativeConfig(),
      rootElementConstraints
    );

    const { allContainers, rootContainer } = this;

    const loaded = rootContainer.loadContent().catch((e) => this.error(e));

    const ready = rootContainer
      .layout()
      .then(() => {
        LiftoffMRAID.onViewable(() => {
          if (rootContainer.invalidated) return;
          allContainers.forEach(({ content }) => {
            if (content) content.start();
          });
        });
      })
      .catch((e) => this.error(e));

    Promise.all([loaded, ready]).then(() => {
      if (!this.loaded) {
        this.loaded = true;
        Liftoff.onMadlibLoaded();
        this.emit("beforeVisible");
        Liftoff.onFirstLayoutFinished();
      }
    });
  }

  static reset() {
    this.productIndex = 0;
    // TODO(brian): Teardown existing components here.
    Liftoff.resetClickParams();
    this.clickEl.classList.remove("active");
    empty(this.layoutEl);
    if (this.didEverApplyLetterboxStyles) {
      [this.layoutEl, this.clickEl].forEach((el) => {
        [
          "left",
          "top",
          "width",
          "height",
          "right",
          "bottom",
          "transform",
        ].forEach((prop) => {
          el.style[prop] = "";
        });
      });
    }
  }

  static parseCreativeConfig(rootEl, config, rootElementConstraints) {
    const componentParams = config.componentParams;

    const { clickTargets, constraints, containers, coordination, css } = config;

    const { rootConstraints, rootPostLayoutTransform } =
      rootElementConstraints || {};

    const traverse = (parent, { id, children }) => {
      const container = new Container(document.createElement("div"), id);
      container.parent = parent;
      container.children = (children || []).map(traverse.bind(null, container));
      container.css = css[id];
      container.constraints = constraints[id];
      if (componentParams[id] != null) {
        let { component, params } = componentParams[id];
        const subcomponents = [];
        const fixValues = (params) => {
          Object.keys(params).forEach((key) => {
            const { type, value } = params[key];
            if (value != null) {
              switch (type) {
                case "array":
                  params[key].value = value.map(fixValues);
                  break;
                case "image_original":
                case "video_original":
                  params[key].value = Liftoff.resolveAsset(type, value);
                  break;
                case "component":
                  const componentName = value.component;
                  const componentParams = fixValues(clone(value.params));
                  const component = Liftoff.Component.new(
                    componentName,
                    componentParams
                  );
                  params[key].value = component;
                  subcomponents.push(component);
                  break;
              }
            }

            params[key] = params[key].value;
          });
          return params;
        };

        params = fixValues(clone(params));
        container.content = Liftoff.Component.new(component, params);
        container.content.on("error", this.error.bind(this));
        // Errors from all subcomponents, regardless of nesting depth, are
        // relayed directly to the top-level component.
        subcomponents.forEach((subcomponent) =>
          subcomponent.relay(container.content, "error")
        );
      }

      this.containerByID[id] = container;
      this.allContainers.push(container);
      return container;
    };

    this.containerByID = {};
    this.allContainers = [];
    this.rootContainer = new Container(rootEl);
    this.rootContainer.parent = new Container(rootEl.parentNode);
    this.rootContainer.children = containers.map(
      traverse.bind(null, this.rootContainer)
    );
    this.rootContainer.css = { overflow: "hidden" };
    this.rootContainer.constraints = rootConstraints || {
      width: "100%",
      height: "100%",
    };
    this.rootContainer.postLayoutTransform = rootPostLayoutTransform;

    this.attachAnchors();
    this.configureCoordination(coordination);
    this.configureClickTargets(clickTargets);

    this.rootContainer.layout();
  }

  static attachAnchors() {
    this.allContainers.forEach((container) => {
      if (container.constraints == null) return;
      container.anchors = ["top", "right", "bottom", "left"]
        .filter((edge) => type(container.constraints[edge]) === "object")
        .map((edge) => {
          return {
            edge,
            anchor: this.containerByID[container.constraints[edge].anchor],
          };
        });
    });
  }

  // Link events with actions.
  static configureCoordination(coordination) {
    coordination
      .filter(({ event }) => event.name != null)
      .forEach(({ event, actions }) => {
        const {
          name: eventName,
          type: eventType,
          source,
          data: eventMacros,
        } = event;

        const actors = actions
          .map((action) => {
            const {
              name: actionName,
              type: actionType,
              target,
              data: actionMacros,
            } = action;
            let actor;
            switch (actionType) {
              case "madlib":
                actor = this;
                break;
              case "component":
                actor = this.containerByID[target].content;
                break;
              case "container":
                actor = this.containerByID[target];
                break;
            }

            return { actionName, actor, actionMacros };
          })
          .filter(({ actionName }) => actionName != null);

        let emitter;
        switch (eventType) {
          case "madlib":
            emitter = this;
            break;
          case "component":
            emitter = this.containerByID[source].content;
            break;
          case "container":
            emitter = this.containerByID[source];
            break;
        }

        const getMacroValues = (eventPayload) => {
          const macroValues = {};
          Object.keys(eventMacros).forEach((key) => {
            const macro = eventMacros[key];
            macroValues[macro] = eventPayload[key];
          });

          return macroValues;
        };

        const expandMacros = (macroValues, actionMacros) => {
          const actionPayload = {};
          Object.keys(actionMacros).forEach((key) => {
            const expansion = Object.keys(macroValues).reduce((s, macro) => {
              // TODO(oscar): Tokenize macro better.
              s = s == null ? "" : s;
              const r = escapeRegExp(macro);
              const sub =
                macroValues[macro] == null ? "" : String(macroValues[macro]);
              return s.replace(new RegExp(r, "g"), sub);
            }, actionMacros[key]);

            actionPayload[key] = expansion;
          });

          return actionPayload;
        };

        const processEventPayload = (eventPayload, actionMacros) => {
          return expandMacros(getMacroValues(eventPayload), actionMacros);
        };

        const dispatchActions = (eventPayload = {}) => {
          // "Waterfall" the `.emit` Promises to process them serially.
          return actors
            .reduce((promise, { actionName, actor, actionMacros }) => {
              const data = processEventPayload(eventPayload, actionMacros);
              return promise.then(() => actor.emit(actionName, data));
            }, Promise.resolve())
            .catch((e) => this.error(e));
        };

        emitter.on(eventName, dispatchActions);
      });
  }

  // Setup click listeners.
  static configureClickTargets(clickTargets) {
    if (clickTargets.length > 0) {
      clickTargets.forEach((id) => {
        ClickInteraction.addListeners(this.containerByID[id].el, (e) => {
          this.handleNavigationClick(e);
        });
      });
    }
    // tre HTML endcards have their own way of handling click events.
    else if (!window.Liftoff.env.is_tre || !window.TreSensa) {
      this.clickEl.classList.add("active");
    }
  }
}

Madlib.init();

Madlib.ClickInteraction = ClickInteraction;

window.Madlib = Madlib;

export default Madlib;



// WEBPACK FOOTER //
// ./src/js/madlib/madlib.js